import { FC, PropsWithChildren, createContext, useState } from "react";
import CollectIcon from "../../assets/icons/Collect.svg";
import DbIcon from "../../assets/icons/Db.svg";
import ChromeIcon from "../../assets/icons/Chrome.svg";
import SalesIcon from "../../assets/icons/Sales.svg";
import MarketingIcon from "../../assets/icons/Marketing.svg";
import RecruitingIcon from "../../assets/icons/Recruiting.svg";
import HelpCenterIcon from "../../assets/icons/HelpCenter.svg";
import BlogIcon from "../../assets/icons/Blog.svg";
import InfoIcon from "../../assets/icons/Info.svg";
import PrivacyIcon from "../../assets/icons/Privacy.svg";
import ContactUsIcon from "../../assets/icons/ContactUs.svg";
import { ROUTES, EXTERNAL_ROUTES } from "../../libs/routing";

export type NavChildOption = {
  icon: string;
  title: string;
  desc: string;
  id: string;
  parentId: string;
  link?: string;
};

export type NavOption = {
  title: string;
  id: string;
  link?: string;
  children?: NavChildOption[];
};

export const HeaderContext = createContext<{
  config: NavOption[];
  openedSubMenu: null | string;
  activeNavId: string | null;
  menuIsOpen: boolean;
  setMenuIsOpen: (val: boolean) => void;
  setOpenedSubMenu: (val: null | string) => void;
}>({
  openedSubMenu: null,
  activeNavId: null,
  menuIsOpen: false,
  setMenuIsOpen() {},
  setOpenedSubMenu() {},
  config: [],
});

export const HeaderProvider: FC<PropsWithChildren> = ({ children }) => {
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const config = [
    {
      title: "Why Powerlead",
      id: "nav-0",
      children: [
        {
          icon: CollectIcon,
          title: "The platform",
          desc: "Collect verified contact information and enrich your CRM.",
          id: "nav-1",
          parentId: "nav-0",
          link: ROUTES.WHY_POWERLEAD,
        },
        {
          icon: DbIcon,
          title: "Unlimited data",
          desc: "Powerlead puts high-quality and actionable data in your hands.",
          id: "nav-2",
          parentId: "nav-0",
          link: ROUTES.OUR_DATA,
        },
        {
          icon: ChromeIcon,
          title: "Chrome extension",
          desc: "Prospect and gather contact information directly from LinkedIn.",
          id: "nav-3",
          parentId: "nav-0",
          link: EXTERNAL_ROUTES.EXTENSION_MARKET,
        },
      ],
    },
    {
      title: "Solutions",
      id: "nav-4",
      children: [
        {
          icon: SalesIcon,
          link: ROUTES.SALES_SOLUTIONS,
          title: "Sales",
          desc: "Unlimited opportunities to connect with your buyers.",
          id: "nav-5",
          parentId: "nav-4",
        },
        {
          icon: MarketingIcon,
          link: ROUTES.MARKETING_SOLUTIONS,
          title: "Marketing",
          desc: "Reach your target audience with verified contact information.",
          id: "nav-6",
          parentId: "nav-4",
        },
        {
          icon: RecruitingIcon,
          link: ROUTES.RECRUITING_SOLUTIONS,
          title: "Recruiting",
          desc: "Find and reach out to top talent with accurate data.",
          id: "nav-7",
          parentId: "nav-4",
        },
      ],
    },
    {
      link: ROUTES.PRICING,
      title: "Pricing",
      id: "nav-8",
    },
    {
      link: ROUTES.SUCCESS_STORIES,
      title: "Success stories",
      id: "nav-9",
    },
    {
      title: "Resources",
      id: "nav-10",
      children: [
        {
          icon: HelpCenterIcon,
          link: EXTERNAL_ROUTES.HELP_CENTER,
          title: "Help center",
          desc: "Connect with our team and get assistance with using the platform.",
          id: "nav-12",
          parentId: "nav-9",
        },
        {
          icon: BlogIcon,
          title: "Blog",
          link: "/blog",
          desc: "Learn more about Powerlead and how to harness the power of the platform.",
          id: "nav-13",
          parentId: "nav-9",
        },
        {
          icon: InfoIcon,
          link: ROUTES.ABOUT,
          title: "About Powerlead",
          desc: "Find out how our unique solutions can help build your business.",
          id: "nav-14",
          parentId: "nav-9",
        },
        {
          icon: PrivacyIcon,
          link: ROUTES.PRIVACY_CENTER,
          title: "Privacy center",
          desc: "Learn about how we collect our data and your rights.",
          id: "nav-15",
          parentId: "nav-9",
        },
        {
          icon: ContactUsIcon,
          link: ROUTES.CONTACT_US,
          title: "Contact us",
          desc: "Get in touch with us to learn about partnership, press, sales, or support.",
          id: "nav-16",
          parentId: "nav-9",
        },
      ],
    },
  ];
  const activeLinksMap = config.reduce((acc: Record<string, string>, item) => {
    if (item.link) {
      acc[item.link] = item.id;
      return acc;
    }

    if (item?.children) {
      item.children.forEach((inner) => {
        if (inner?.link) {
          acc[inner.link] = inner.parentId;
        }
      });
    }
    return acc;
  }, {});
  const [openedSubMenu, setOpenedSubMenu] = useState<null | string>(null);
  const activeNavId = activeLinksMap[window.location.pathname] || null;

  return (
    <HeaderContext.Provider
      value={{
        openedSubMenu,
        setOpenedSubMenu,
        activeNavId,
        config,
        menuIsOpen,
        setMenuIsOpen,
      }}
    >
      {children}
    </HeaderContext.Provider>
  );
};
